import {h, Fragment} from "preact"
/**
 * Nosto Product Card:
 * https://my.nosto.com/admin/shopify-24139303/search-code/editor
 */

 import {SerpElement, useCurrencyFormatting} from '@nosto/preact';
 import {getCustomField, calculateDiscount} from '../helpers';
 
 export default function Product({product}) {
   const {format} = useCurrencyFormatting();
   const sortedSkus = sortSkus(product?.skus);
 
   const formId = `product-card-quick-add-form-${product.productId}`;
 
   const productHandle = product.url.split('/').pop().split('?')[0];
 
   return (
     <SerpElement as="li" hit={product} data-grid-item="" class="results-grid__tile">
       <product-card class="product-card ns" data-product-card="">
         <div className="product-card__hero">
           <div className="product-card__image">
             <a href={product.url}>
               <div className="component-image__wrapper aspect-ratio aspect-ratio--portrait">
                 <img
                   className="component-image__image"
                   src={`${product.imageUrl}&width=600`}
                   loading="lazy"
                   alt={product.name}
                 />
               </div>
 
               {product.alternateImageUrls?.length > 0 && (
                 <div className="product-card__image--secondary">
                   <div className="component-image__wrapper aspect-ratio aspect-ratio--portrait">
                     <img
                       className="component-image__image"
                       src={`${product?.alternateImageUrls?.[0] ?? product.imageUrl}&width=600`}
                       loading="lazy"
                       alt={product.name}
                     />
                   </div>
                 </div>
               )}
             </a>
           </div>
 
           <div className="product-card__actions">
             <product-card-quick-add class="product-card__quick-add">
               <form
                 method="post"
                 action="/cart/add"
                 id={formId}
                 acceptCharset="UTF-8"
                 className="product-card__form"
                 encType="multipart/form-data"
                 data-product-card-form-desktop=""
               >
                 <input type="hidden" name="quantity" value="1" />
 
                 <fieldset className="product-card__quick-add-variants" data-variant-count={sortedSkus.length}>
                   <legend className="visually-hidden">Select a variant</legend>
                   {sortedSkus.map((sku) => {
                     const variantInputId = `variant_${sku.id}_${product.productId}`;
                     const variantLabel = sku.customFields.find((field) => field.key === 'size')?.value;
 
                     return (
                       <div data-testid={`product-card-quick-add-variant-${sku.id}`} key={sku.id}>
                         <input
                           name="id"
                           id={variantInputId}
                           value={sku.id}
                           type="radio"
                           className="visually-hidden"
                           disabled={sku.availability === 'OutOfStock'}
                         />
                         <label
                           htmlFor={`variant_${sku.id}_${product.productId}`}
                           className={`product-card__quick-add-variant-label ${sku.availability === 'OutOfStock' ? 'disabled' : ''}`}
                           data-state="initial"
                           data-variant-label
                           data-variant-id={sku.id}
                         >
                           <span className="product-card__variant-title">{variantLabel || sku.title}</span>
                         </label>
                       </div>
                     );
                   })}
                 </fieldset>
               </form>
             </product-card-quick-add>
 
             <product-quick-view data-product-handle={productHandle}>
               <button className="product-card__quick-view btn-none" data-trigger>
                 <svg
                   className="icon icon--bag-sm"
                   aria-hidden="true"
                   width="16"
                   height="16"
                   viewBox="0 0 16 16"
                   fill="none"
                   xmlns="http://www.w3.org/2000/svg"
                 >
                   <path
                     fillRule="evenodd"
                     clipRule="evenodd"
                     d="M11 6H14V14H2V6H5V3H11V6ZM3 13H13V7H3V13ZM6 6H10V4H6V6Z"
                     fill="currentColor"
                   />
                 </svg>
                 <span className="visually-hidden">Select a variant</span>
               </button>
             </product-quick-view>
           </div>
         </div>
 
         <div className="product-card__meta">
           {product.url.includes('neverland') && (
             <div className="h5 product__vendor" aria-label="Vendor">
               {product.brand}
             </div>
           )}
 
           <h5 className="product-card__title">
             <a href={product.url}>{product.name}</a>
           </h5>
           <div className="product-card__price">
             <a href={product.url}>
               {product.price != product.listPrice ? (
                 <>
                   <span data-product-price="" className="product-card__sale-price">
                     {format(product.price)}
                   </span>
                   <span className="visually-hidden">Regular price</span>
                   <s data-compare-price="">{format(product.listPrice)}</s>
                 </>
               ) : (
                 <span data-product-price="">{format(product.price)}</span>
               )}
             </a>
           </div>
           {getCustomField('custom-discount', product) != false && (
             <div
               className="product__discount-message"
               style={`color: ${getCustomField('custom-discount_colour', product)}`}
             >
               {getCustomField('custom-discount', product)}
             </div>
           )}
         </div>
 
         {getCustomField('badge-label', product) != false && product.price == product.listPrice && (
           <div className="product-badges product-card__badges" data-product-badges="">
             <div
               className="badge"
               style={`background-color: ${getCustomField('badge-background_color', product)}; color: ${getCustomField('badge-text_color', product)};`}
             >
               {getCustomField('badge-label', product)}
             </div>
           </div>
         )}
 
         {getCustomField('badge-label', product) == false && product.price < product.listPrice && (
           <div className="product-badges product-card__badges" data-product-badges="">
             <div className="badge badge--on-sale">{calculateDiscount(product.price, product.listPrice)}% OFF</div>
           </div>
         )}
       </product-card>
     </SerpElement>
   );
 }
 
 function sortSkus(skus) {
   if (!skus) return [];
 
   const sizeOrder = [
     'XXS',
     'XS',
     'S',
     'M',
     'L',
     'XL',
     'XXL',
     'XXXL',
     '28',
     '29',
     '30',
     '31',
     '32',
     '33',
     '34',
     '36',
     '38',
     '40',
   ];
 
   return skus?.slice().sort((a, b) => {
     const sizeA = a?.customFields.find((field) => field.key === 'size')?.value || '';
     const sizeB = b?.customFields.find((field) => field.key === 'size')?.value || '';
 
     return sizeOrder.indexOf(sizeA) - sizeOrder.indexOf(sizeB);
   });
 }
 